import { createCoupon, fetchCoupon, updateCoupon } from 'admin/actions/coupon';
import { useEffect, useState } from 'react';

type Coupon = Awaited<ReturnType<typeof fetchCoupon>>;

const defaultValues: Partial<Coupon> = {
  is_disabled: false,
  is_public: true,
  is_shop_specified: false,
  usage_limit: 1,
  name: '',
  starts_at: null,
  ends_at: null,
  display_starts_at: null,
  display_ends_at: null,
};

export type CouponError = {
  message?: string;
  [key: string]: unknown;
};

export type CouponParams = {
  name?: string;
  description?: string;
  discount_amount?: number | null;
  discount_percentage?: number | null;
  discount_upper_limit?: number | null;
  starts_at?: string | null;
  ends_at?: string | null;
  display_starts_at?: string | null;
  display_ends_at?: string | null;
  is_disabled?: boolean;
  is_hidden?: boolean;
  is_public?: boolean;
  code?: string | null;
  usage_limit?: number | null;
  min_amount?: number | null;
  issued_shop_id?: string;
  private_money_id?: string;
  is_shop_specified?: boolean;
  available_shop_ids?: string;
  coupon_image?: File | Blob | string | null;
};

const useCoupon = (couponId: string) => {
  const [coupon, setCoupon] = useState<Coupon>(defaultValues as Coupon);
  const [error, setError] = useState<CouponError>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getCoupon = async (id: string): Promise<void> => {
    setIsLoading(true);
    await fetchCoupon(id)
      .then((c: Coupon) => {
        setCoupon(c);
      })
      .catch((maybeError) => Promise.resolve(maybeError).then((e: CouponError) => setError(e)))
      .finally(() => setIsLoading(false));
  };

  const submitUpdate = async (
    id: string,
    params: CouponParams,
    onSuccess: () => void
  ): Promise<void> => {
    setIsLoading(true);
    await updateCoupon(id, params)
      .then((c: Coupon) => {
        setCoupon(c);
        onSuccess();
      })
      .catch((maybeError) => Promise.resolve(maybeError).then((e: CouponError) => setError(e)))
      .finally(() => setIsLoading(false));
  };

  const submitCreate = async (params: CouponParams, onSuccess: () => void): Promise<void> => {
    setIsLoading(true);
    await createCoupon(params)
      .then((c: Coupon) => {
        setCoupon(c);
        onSuccess();
      })
      .catch((maybeError) => Promise.resolve(maybeError).then((e: CouponError) => setError(e)))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (typeof couponId === 'string') {
      if (couponId !== 'create') {
        getCoupon(couponId);
      }
    }
  }, [couponId]);

  return { coupon, submitUpdate, submitCreate, error, isLoading };
};

export default useCoupon;
